import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import useAxios from "./useAxios";
import { setBusinessData } from "../redux/slices/memberSlice";

// The business theme (logo, name, colors) is the same for the whole session, so
// it must be fetched at most once - not on every page / layout component mount.
//
// These module-level vars dedupe the call across every component that uses the
// hook: `themeCache` holds the resolved theme, `inFlight` holds the single
// pending request so concurrent mounts share it. sessionStorage keeps it across
// full page reloads within the same browser session.
let themeCache = null;
let inFlight = null;

const readSessionCache = () => {
  try {
    const raw = sessionStorage.getItem("businessTheme");
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

// Exposed so callers can drop the cache if the theme could have changed.
export const clearBusinessThemeCache = () => {
  themeCache = null;
  inFlight = null;
  sessionStorage.removeItem("businessTheme");
};

export default function useBusinessTheme() {
  const axios = useAxios();
  const dispatch = useDispatch();
  const [theme, setTheme] = useState(() => themeCache || readSessionCache());

  useEffect(() => {
    if (!sessionStorage.getItem("authToken")) return;

    let active = true;
    const apply = (data) => {
      if (!data) return;
      themeCache = data;
      if (active) setTheme(data);
      dispatch(setBusinessData(data));
    };

    // Already fetched this session (memory or sessionStorage) -> no API call.
    const cached = themeCache || readSessionCache();
    if (cached) {
      apply(cached);
      return () => {
        active = false;
      };
    }

    // First caller fires the request; later callers reuse the same promise.
    if (!inFlight) {
      inFlight = axios({
        url: "/admin/business/get-business-theme",
        method: "GET",
        customAuthToken: sessionStorage.getItem("authToken"),
        disableError: true,
        disableRedirect: true,
      })
        .then((response) => {
          inFlight = null;
          if (response?.status) {
            try {
              sessionStorage.setItem(
                "businessTheme",
                JSON.stringify(response.data)
              );
            } catch (e) {}
            return response.data;
          }
          return null;
        })
        .catch(() => {
          inFlight = null;
          return null;
        });
    }

    inFlight.then(apply);

    return () => {
      active = false;
    };
  }, []);

  return theme;
}
